import { useEffect, useState } from 'react';
import { Box, Card, RailzBusinessAccount, withStyles, PulloverSelect, convertToNumber } from 'vopay';
import { Title, Button } from 'UI';

import { useForm } from '../../Form/utils';
import { useToast } from '@/providers/Toast.provider';
import useRailzCreateInvoices from '@/hooks/useApi/useRailzCreateInvoices';
import useRailzCustomers from '@/hooks/useApi/useRailzCustomers';
import useRailzInventory from '@/hooks/useApi/useRailzInventory';
import useRailzTaxRates from '@/hooks/useApi/useRailzTaxRates';
import useRailzTrackingCategories from '@/hooks/useApi/useRailzTrackingCategories';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import InvoiceHeader from './InvoiceHeader/InvoiceHeader';
import { useClientData } from '@/providers/ClientData.provider';
import InvoicesTable from './InvcoicesTable/InvoicesTable';
import InvoiceBalances from './InvoiceBalances/InvoiceBalances';
import {
  buildInvoiceFormRows,
  buildInvoiceNoteRows,
  buildInvoiceNumberRows,
  InvoiceSubmission,
  LineItem,
} from './buildInvoicesForm.helper';
import styles from './CreateInvoicePage.module.scss';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchRailzInvoices from '@/hooks/useApi/useFetchRailzInvoices';
import { addDays, addYears, format } from 'date-fns';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
const classes = withStyles(styles);

const CreateInvoicePage: React.FC = () => {
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Software' });
  const { customers, fetchCustomers } = useRailzCustomers();
  const { inventory, fetchInventory } = useRailzInventory();
  const { taxRates, fetchTaxRates } = useRailzTaxRates();
  const { trackingCategories, fetchTrackingCategories } = useRailzTrackingCategories();
  const [railzBusinessAccountResponse, fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [currentRailzAccount, setCurrentRailzAccount] = useState<RailzBusinessAccount | undefined>();
  const [railzInvoicesResponse, fetchRailzInvoices] = useFetchRailzInvoices({});
  const { response } = useClientData();
  const { createInvoice } = useRailzCreateInvoices();
  const [isTableValid, setIsTableValid] = useState(false);
  const history = useHistory();
  const toast = useToast();
  const [previousTerms, setPreviousTerms] = useState<
    'Due on Receipt' | 'Custom' | 'Net 15' | 'Net 30' | 'Net 60' | 'Net 80' | 'Net 90' | ''
  >('');
  const [previousPostedDate, setPreviousPostedDate] = useState(new Date().getTime().toString());
  const [previousDueDate, setPreviousDueDate] = useState('');
  const [taxCalculation, setTaxCalculation] = useState<'inclusive' | 'exclusive' | 'outOfScope'>('exclusive');
  const [lines, setLines] = useState<LineItem[]>([]);
  const taxCalculationOptions: {
    label: string;
    value: 'inclusive' | 'exclusive' | 'outOfScope';
  }[] = [
    { label: t('Common.taxCalculation.inclusive', { defaultValue: 'Inclusive' }), value: 'inclusive' },
    { label: t('Common.taxCalculation.exclusive', { defaultValue: 'Exclusive' }), value: 'exclusive' },
    { label: t('Common.taxCalculation.outOfScope', { defaultValue: 'Out of Scope' }), value: 'outOfScope' },
  ];

  useEffect(() => {
    fetchRailzBusinessAccount({});
  }, []);

  useEffect(() => {
    if (railzBusinessAccountResponse) {
      const currentAccount: RailzBusinessAccount | undefined = Object.values(
        railzBusinessAccountResponse.Accounts || {},
      )[0];
      setCurrentRailzAccount(currentAccount);
    }
  }, [railzBusinessAccountResponse]);

  useEffect(() => {
    if (currentRailzAccount) {
      fetchCustomers({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchInventory({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchTaxRates({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchTrackingCategories({
        BusinessName: currentRailzAccount.BusinessName,
        ServiceName: currentRailzAccount.ServiceName,
      });
      fetchRailzInvoices({
        BusinessName: currentRailzAccount.BusinessName || '',
        ServiceName: currentRailzAccount.ServiceName || '',
        StartDate: format(addYears(new Date(), -20), 'yyyy-MM-dd'),
        EndDate: format(addYears(new Date(), 5), 'yyyy-MM-dd'),
        Limit: 1,
        Status: 'open',
      });
    }
  }, [currentRailzAccount]);
  const invoiceNumber = buildInvoiceNumberRows(t);
  const invoiceNote = buildInvoiceNoteRows(t);
  const invoiceForm = buildInvoiceFormRows(customers || [], t);

  const { ctx, renderFormElements } = useForm<InvoiceSubmission, InvoiceSubmission>({
    config: [...invoiceNumber, ...invoiceForm, ...invoiceNote],
    initialValues: {
      InvoiceNumber: '',
      PostedDate: new Date().getTime().toString(),
      DueDate: '',
      CustomerRefID: '',
      CustomerEmail: '',
      CustomerMailingAddress: '',
      Terms: '',
      Memo: '',
      Lines: [],
    },
    onSubmit: async (serializedData) => {
      if (!currentRailzAccount) return;
      const response = await createInvoice({
        BusinessName: currentRailzAccount?.BusinessName,
        ServiceName: currentRailzAccount?.ServiceName,
        Invoice: {
          ...serializedData,
          Currency: 'CAD',
          CurrencyRate: 1,
          CustomerRefID: convertToNumber(serializedData.CustomerRefID) || 0,
          Lines: lines.map((line) => {
            return {
              ...line,
              Amount: convertToNumber(line.Amount) || 0,
              Quantity: convertToNumber(line.Quantity) || 0,
              InventoryRefID: convertToNumber(line.InventoryRefID) || 0,
              Rate: convertToNumber(line.Rate) || 0,
              TaxRateID: convertToNumber(line.TaxRateID || '') || 0,
              TrackingCategoryID: convertToNumber(line.TrackingCategoryID || '') || 0,
            };
          }),
        },
      });
      if (!response.Success) {
        alertDialog.error('Error', response.ErrorMessage);
      } else {
        history.push('/receivables');
        toast.success(t('CreateInvoice.successMessage', { defaultValue: 'Invoice created successfully' }));
      }
    },
  });
  useEffect(() => {
    if (railzInvoicesResponse?.Success) {
      const latestInvoice = railzInvoicesResponse.Invoices?.[0];
      if (latestInvoice) {
        const invoiceNumber = Number(latestInvoice.invoiceNumber);
        if (!isNaN(invoiceNumber)) {
          ctx.setFieldValue('InvoiceNumber', String(invoiceNumber + 1));
        } else {
          ctx.setFieldValue('InvoiceNumber', '');
        }
      }
    }
  }, [railzInvoicesResponse]);

  useEffect(() => {
    if (ctx.values.Terms !== previousTerms) {
      setPreviousTerms(ctx.values.Terms);
      let postedDate = new Date(Number(ctx.values.PostedDate));
      if (ctx.values.PostedDate === '') {
        ctx.setFieldValue('PostedDate', new Date().getTime().toString());
        setPreviousPostedDate(new Date().getTime().toString());
        postedDate = new Date();
      }

      if (ctx.values.Terms === 'Net 15') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 15).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 15).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 30') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 30).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 30).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 60') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 60).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 60).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 80') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 80).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 80).getTime().toString());
      }
      if (ctx.values.Terms === 'Net 90') {
        ctx.setFieldValue('DueDate', addDays(postedDate, 90).getTime().toString());
        setPreviousDueDate(addDays(postedDate, 90).getTime().toString());
      }
      if (ctx.values.Terms === 'Due on Receipt') {
        ctx.setFieldValue('DueDate', postedDate.getTime().toString());
        setPreviousDueDate(postedDate.getTime().toString());
      }
    }
  }, [ctx.values.Terms]);
  useEffect(() => {
    if (ctx.values.PostedDate !== previousPostedDate) {
      setPreviousPostedDate(ctx.values.PostedDate);
      ctx.setFieldValue('Terms', 'Custom');
    }
  }, [ctx.values.PostedDate]);
  useEffect(() => {
    if (ctx.values.DueDate !== previousDueDate) {
      setPreviousDueDate(ctx.values.DueDate);
      ctx.setFieldValue('Terms', 'Custom');
    }
  }, [ctx.values.DueDate]);

  return (
    <div>
      <Box flx="col" gapSize="24px">
        <Title level="1">{t('CreateInvoice.title', { defaultValue: 'Create Invoice' })}</Title>
        <Card style={{ padding: '32px' }}>
          <InvoiceHeader
            name={response.AccountName}
            invoiceField={
              renderFormElements({
                config: invoiceNumber,
                override(fieldData) {
                  return fieldData.template;
                },
              })[0]
            }
          />

          <div className={classes('form')}>
            {renderFormElements({
              config: invoiceForm,
            })}
          </div>

          <div className={classes('dashed')} />
          <div className={classes('tableContainer')}>
            <Box flx="col" gapSize="14px">
              <div className={classes('popoverContainer')}>
                {t('Common.amountsAre', { defaultValue: 'Amounts are' })}
                <PulloverSelect
                  value={taxCalculation}
                  onChange={(value) => {
                    setTaxCalculation(value as 'inclusive' | 'exclusive' | 'outOfScope');
                  }}
                >
                  {taxCalculationOptions.map((option) => (
                    <PulloverSelect.Option key={option.value} value={option.value}>
                      {option.label}
                    </PulloverSelect.Option>
                  ))}
                </PulloverSelect>
              </div>
              {inventory && taxRates && trackingCategories && (
                <InvoicesTable
                  onChange={(invoiceItems, isValid) => {
                    ctx.setFieldValue('Lines', invoiceItems);
                    setLines(invoiceItems);
                    setIsTableValid(isValid);
                  }}
                  taxCalculation={taxCalculation}
                  afterHasTouched={!!ctx.touched.Memo}
                  inventory={inventory}
                  salesTaxes={taxRates}
                  trackingCategories={trackingCategories}
                />
              )}
            </Box>
          </div>
          <div className={classes('dashed')} />
          <div className={classes('totalsContainer')}>
            <InvoiceBalances
              taxRates={taxRates || []}
              salesTax={ctx.values.Lines.map((line) => {
                return {
                  amount: convertToNumber(line.Amount) || 0,
                  salesTaxId: line.TaxRateID,
                };
              })}
              taxCalculation={taxCalculation}
              textArea={
                renderFormElements({
                  config: invoiceNote,
                })[0]
              }
            />
          </div>
          <div className={classes('dashed')} />
        </Card>

        <Box base="8" gap="sm" jc="fe" ai="center">
          <Button
            onClick={() => {
              history.goBack();
            }}
          >
            {t('Common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={() => {
              ctx.handleSubmit();
            }}
            colorScheme="primary"
            disabled={!isTableValid || !ctx.isValid}
          >
            {t('Common.create', { defaultValue: 'Create' })}
          </Button>
        </Box>
      </Box>

      {/* Add your form or other components here */}
    </div>
  );
};

export default CreateInvoicePage;
